import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";

import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";

import NewModuleGroupDialog from "./NewModuleGroupDialog";
import MediaBrowser from "../creator-application/common/MediaBrowser";
import ModuleGroupsTable from "./ModuleGroupsTable";
import { useModuleGroups, useModuleGroupDelete } from "./moduleGroupsService";

import SearchBar from "../creator-application/common/SearchBar";
import TotalsHeader from "../creator-application/common/TotalsHeader";

import { useNavigate, useResolvedPath } from "react-router-dom";
import { filter, test, join } from "ramda";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      display: "flex",
      flexDirection: "column",
      height: "95%",
      padding: "0",
      borderRadius: "20px",
      border: "3px white solid",
    },

    panel: {
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    toolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: 60,
      borderWidth: 2,
      borderStyle: "solid",
      borderColor: theme.palette.primary.light,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.main,
      padding: theme.spacing(4),
    },

    leftContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      minWidth: 200,
    },

    rightContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      flexGrow: 1,
    },

    title: {
      flexGrow: 1,
      display: "flex",
      alignItems: "center",
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    count: {
      marginRight: 10,
      fontSize: "2.0rem",
      fontWeight: "bold",
    },

    newButton: {
      textTransform: "uppercase",
      color: "#127DA9",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },

    listContainer: {
      height: "calc(100vh - 275px)",
      overflow: "auto",
    },

    filters: {
      minWidth: 650,
      width: "auto",
      paddingTop: 3,
      marginLeft: theme.spacing(2),
    },
  }));

const ModuleGroupsBrowser = () => {
  const path = useResolvedPath(".").pathname;
  const navigate = useNavigate();
  const [scrollVisible, setScrollVisible] = useState(true);
  const classes = useStyles(scrollVisible)();
  const reduxDispatch = useDispatch();
  const [page, setPage] = useState(0);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const [moduleGroups, setModuleGroups] = useState([]);
  const initialModuleGroups = useModuleGroups();

  useEffect(() => {
    if (
      initialModuleGroups.isError &&
      initialModuleGroups.error.status === 401
    ) {
      reduxDispatch(logOut());
    }

    if (initialModuleGroups.isSuccess) {
      setModuleGroups(initialModuleGroups.data);
      setScrollVisible(true);
    }
  }, [
    initialModuleGroups.data,
    initialModuleGroups.isError,
    initialModuleGroups.error,
    reduxDispatch,
    initialModuleGroups.isSuccess,
    initialModuleGroups.isLoading,
  ]);

  const moduleGroupDelete = useModuleGroupDelete();

  const deleteModuleGroup = useCallback(
    async moduleGroupId => {
      try {
        await moduleGroupDelete.mutateAsync({
          resourceIds: { id: moduleGroupId },
        });
        initialModuleGroups.refetch();
        setPage(0);
      } catch (e) {
        console.log(e);
      }
    },
    [moduleGroupDelete, initialModuleGroups],
  );

  // TODO: bad solution
  // The pagination and offset from API is not working as intended.
  // When I set param as ?limit=10, API returned 2 module groups even though more exist
  // And there is a version issue with MUI TablePagination where onPageChange returns undefined for newPage
  // So this is my hacky solution for now
  // it works as intended but is not elegant
  const handleChangePage = (event, newPage) => {
    event.currentTarget.attributes["aria-label"].value === "Next page"
      ? setPage(page + 1)
      : setPage(page - 1);
  };

  //-- Handle New Module Group Dialog
  const [newModuleGroupDialogOpen, setNewModuleGroupDialogOpen] = useState(
    false,
  );

  const handleNewModuleGroup = moduleGroupId => {
    setNewModuleGroupDialogOpen(false);
    navigate(`${path}/${moduleGroupId}`);
  };

  //-- Handle Search
  const [appliedQuery, setAppliedQuery] = useState([]);
  const handleQueryChange = updatedQuery => {
    setAppliedQuery(updatedQuery);
    const queries = new RegExp(join("|", updatedQuery), "ig");
    const filteredModuleGroups = filter(
      moduleGroup => test(queries, moduleGroup.moduleGroupName),
      initialModuleGroups.data,
    );
    setModuleGroups(filteredModuleGroups);
  };

  return (
    <Container id="module-groups-browser" classes={{ root: classes.root }}>
      <NewModuleGroupDialog
        open={newModuleGroupDialogOpen}
        onCancel={useCallback(() => setNewModuleGroupDialogOpen(false), [])}
        onOk={handleNewModuleGroup}
      />

      <MediaBrowser
        loaded={initialModuleGroups.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <SearchBar query={appliedQuery} onQueryChange={handleQueryChange} />
        }
        totalsHeader={
          <TotalsHeader
            countText="module group"
            count={initialModuleGroups.isSuccess ? moduleGroups.length : "-"}
            selectedActions={
              <div>
                <Button
                  variant={"text"}
                  startIcon={<AddIcon />}
                  onClick={() => setNewModuleGroupDialogOpen(true)}
                >
                  New Module Group
                </Button>
              </div>
            }
          />
        }
        mediaTable={
          <ModuleGroupsTable
            moduleGroupsList={moduleGroups}
            onModuleGroupDelete={deleteModuleGroup}
            total={moduleGroups.length}
            page={page}
            rowsPerPage={10}
            onPageChange={handleChangePage}
            onScrollVisible={handleScrollVisible}
          />
        }
      />
    </Container>
  );
};

export default ModuleGroupsBrowser;
